import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Modal from '../../../../shared/_TBComponents/Modal';
import Icon from '../../../../shared/_TBComponents/Icon';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import TYPE_OPTIONS from './exerciseTypeOptions';
import { mergeExercises } from '../../../calendar/ducks/calendarActions';
import { decodeVideoUrl } from '../../../../shared/utils/decodeHtmlEntities';
import resolveIframelyEmbed from '../../../../shared/utils/resolveIframelyEmbed';

// Row-action merge wizard: the clicked row is the source (merged away/deleted);
// pick a same-type target, resolve differing fields, confirm.

const TRACK_LABELS = {
  1: 'Max Weight (1RM)',
  2: 'Highest Reps',
  3: 'Max Weight (3RM)',
  4: 'Highest Weight',
  5: 'Max Weight (5RM)',
};

// Must mirror the table's ATTRIBUTE_DEFS so the confirm step shows the same chips.
const ATTRIBUTE_DEFS = [
  { key: 'eachSide', label: 'Each Side' },
  { key: 'barSpeed', label: 'Velocity' },
  { key: 'peakPower', label: 'Peak Power' },
  { key: 'liftRepsOnly', label: 'Bodyweight' },
  { key: 'liftComp', label: 'Completion' },
  { key: 'liftCoachComp', label: 'Coach Comp' },
];

const TYPE_LABELS = Object.fromEntries(
  TYPE_OPTIONS.filter((opt) => opt.value !== 'all').map((opt) => [opt.value, opt.label]),
);

const hasVideo = (e) => !!(e && e.videoUrl);

// Pull an 11-char video id from common YouTube URL shapes; non-YouTube URLs
// fall back to the plain dimmed tile.
const getYoutubeThumb = (url) => {
  const match = String(url).match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([A-Za-z0-9_-]{11})/,
  );
  return match ? `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg` : null;
};

// Thumbnail for embed links (Vimeo, Drive, …) a <video> element can't decode —
// same iframe.ly service the exercise form uses; resolves null if unavailable.
const fetchEmbedThumb = (url) => resolveIframelyEmbed(url)
  .then(({ thumbnailUrl }) => thumbnailUrl)
  .catch(() => null);

// `get` doubles as the option label on the resolve step, so each value must
// read on its own — a video is only identifiable by the exercise it's from.
const FIELDS = [
  {
    key: 'name', label: 'Exercise Name', get: (e) => e.name || '—', empty: (e) => !e.name,
  },
  {
    key: 'tracking', label: 'Tracking', get: (e) => TRACK_LABELS[e.track] || 'None', empty: (e) => !e.track,
  },
  {
    key: 'description', label: 'Description', get: (e) => (e.description && String(e.description).trim()) || '—', empty: (e) => !(e.description && String(e.description).trim()),
  },
  {
    key: 'video', label: 'Video', get: (e) => (hasVideo(e) ? `Video from ${e.name}` : 'No video'), empty: (e) => !hasVideo(e),
  },
];

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding: 24px 0;
  font-family: "Nunito Sans";
  border-radius: 8px;
  box-shadow: inset 0 0 0 1px #D6D6D6;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
`;

const Title = styled.h2`
  margin: 0;
  font-family: "Nunito Sans";
  font-size: 24px;
  font-weight: 900;
  letter-spacing: -0.24px;
  color: #1A1A1A;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

const Stepper = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 8px 32px;

  @media (max-width: 767px) {
    padding: 8px 16px;
  }
`;

const StepGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const StepBubble = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  background: ${(p) => (p.reached ? '#1787FB' : '#E0E0E0')};
  color: ${(p) => (p.reached ? '#FFFFFF' : '#929292')};
`;

const StepLabel = styled.span`
  font-family: "Nunito Sans";
  font-size: 12px;
  font-weight: 700;
  text-align: center;
  color: ${(p) => (p.reached ? '#1A1A1A' : '#929292')};

  /* Long labels ("Choose Exercise Details") wrap instead of widening the group. */
  @media (max-width: 767px) {
    max-width: 88px;
  }
`;

const StepConnector = styled.div`
  flex: 1;
  height: 2px;
  border-radius: 1px;
  margin-top: 15px;
  background: ${(p) => (p.done ? '#1787FB' : '#E0E0E0')};
`;

const Divider = styled.div`
  height: 1px;
  background: #D6D6D6;
  width: 100%;
`;

const StepBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: ${(p) => (p.wide ? '8px 32px' : '8px 64px')};

  /* The modal is 60vw below ~1170px viewports — give the content back the
     gutter space as it shrinks. */
  @media (max-width: 1200px) {
    padding: 8px 32px;
  }

  @media (max-width: 767px) {
    padding: 8px 16px;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.gap || '4px'};
`;

const FieldLabel = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #1A1A1A;
`;

// The source box and the target select sit in identical rows so the pair reads
// as one before/after comparison, with the outcome called out alongside each.
const ChoiceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  /* On narrow screens the "will be deleted/kept" callout drops below the field. */
  flex-wrap: wrap;
`;

const OutcomeText = styled.span`
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: 400;
  color: #1A1A1A;
  white-space: nowrap;
`;

// Static (non-interactive) box showing the source exercise — it's fixed, not
// chosen, so it doesn't reuse the clickable OptionCard styling.
const SourceBox = styled.div`
  flex: 1;
  min-width: 0;
  border: 1px solid #D6D6D6;
  border-radius: 4px;
  background: #FFFFFF;
  padding: 6px 56px 6px 16px;
  font-size: 16px;
  line-height: 1.5;
  color: #1A1A1A;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 767px) {
    flex-basis: 100%;
    padding-right: 16px;
  }
`;

/* 424px when the modal is full-size; shrinks with the modal (it's 60vw below
   ~1170px viewports) so the "will be kept" callout stays on the same line. */
const SelectWrap = styled.div`
  flex: 1 1 260px;
  max-width: 424px;

  @media (max-width: 767px) {
    flex-basis: 100%;
    max-width: none;
  }
`;

const HelperText = styled.p`
  margin: 0;
  padding: 4px 0;
  font-family: "Nunito Sans";
  font-size: 16px;
  color: #1A1A1A;
`;

const IntroText = styled.p`
  margin: 0;
  font-family: "Nunito Sans";
  font-size: 16px;
  color: #1A1A1A;
`;

// The two candidate values sit side by side so each question reads as a single
// left/right comparison instead of a stack of look-alike rows.
const OptionList = styled.div`
  display: flex;
  gap: 16px;
  align-items: stretch;

  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const OptionCard = styled.button`
  flex: 1;
  min-width: 0;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: ${(p) => (p.selected ? '2px solid #1787FB' : '1px solid #D6D6D6')};
  background: ${(p) => (p.selected ? 'rgba(23, 135, 251, 0.1)' : '#FFFFFF')};
  border-radius: 4px;
  padding: ${(p) => (p.selected ? '7px' : '8px')};
  cursor: pointer;
  font-family: "Nunito Sans";

  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

const OptionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`;

const Radio = styled.span`
  width: 20px;
  height: 20px;
  margin: 2px;
  border-radius: 50%;
  border: 2px solid ${(p) => (p.selected ? '#1787FB' : '#D6D6D6')};
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #1787FB;
    display: ${(p) => (p.selected ? 'block' : 'none')};
  }
`;

/* Label keeps one weight in both states so text never reflows. Pure black
   (vs the app's #1A1A1A) for contrast on the selected card's blue tint. */
const OptionValue = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 16px;
  line-height: 1.5;
  color: #000000;
  font-weight: 400;
`;

const VideoPreview = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 80px;
  border-radius: 4px;
  background: ${(p) => (p.thumb
    ? `linear-gradient(rgba(68, 68, 68, 0.5), rgba(68, 68, 68, 0.5)), url(${p.thumb}) center / cover no-repeat`
    : 'rgba(68, 68, 68, 0.5)')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

// First-frame preview for non-YouTube URLs. The #t=0.1 seek fragment nudges
// Safari/Chrome into painting a real frame instead of a black box with preload='metadata'.
const FirstFrame = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
`;

// Same 50% dim the YouTube-thumbnail background applies, so the white play
// icon stays legible over a bright first frame.
const FrameShade = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(68, 68, 68, 0.5);
  z-index: 1;
`;

const PlayIconWrap = styled.span`
  position: relative;
  z-index: 2;
  display: flex;
`;

const SummaryCard = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 1px solid #D6D6D6;
  border-radius: 4px;
  padding: 8px 16px;
`;

const SummaryName = styled.span`
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: 700;
  color: #1A1A1A;
`;

const KeptCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  border: 1px solid #D6D6D6;
  border-radius: 4px;
  padding: 16px;
`;

const KeptHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const KeptDetails = styled.div`
  display: flex;
  gap: 32px;
  align-items: flex-start;

  @media (max-width: 767px) {
    gap: 16px;
  }
`;

const DetailLabels = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 85px;
  flex: none;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
  color: #1A1A1A;
`;

const DetailValues = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
  font-size: 16px;
  line-height: 1.5;
  color: #1A1A1A;
`;

const TagChips = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const TagChip = styled.span`
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  border-radius: 4px;
  background: #E0E0E0;
  font-family: "Nunito Sans";
  font-size: 16px;
  color: #1A1A1A;
  white-space: nowrap;
`;

const NoteText = styled.p`
  margin: 0;
  font-family: "Nunito Sans";
  font-size: 16px;
  color: #1A1A1A;
`;

const FooterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 0 24px;

  @media (max-width: 767px) {
    padding: 0 16px;
  }
`;

const FooterButton = styled.button`
  min-width: 120px;
  height: 36px;
  padding: 6px 16px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.48px;
  cursor: ${(p) => (p.disabled ? 'default' : 'pointer')};
  border: ${(p) => (p.primary ? 'none' : '1px solid #D6D6D6')};
  background: ${(p) => {
    if (!p.primary) return '#FFFFFF';
    return p.disabled ? '#E0E0E0' : '#1A1A1A';
  }};
  color: ${(p) => (p.primary ? '#FFFFFF' : '#1A1A1A')};

  &:hover { opacity: ${(p) => (p.disabled ? 1 : 0.85)}; }
  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

// react-select's defaults are close to the app's 33px / 4px-radius inputs
// but not exact.
const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: '33px',
    borderColor: '#D6D6D6',
    borderRadius: '4px',
    boxShadow: 'none',
    '&:hover': { borderColor: '#D6D6D6' },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 8px 0 16px',
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: '16px',
    color: '#929292',
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: '16px',
    color: '#1A1A1A',
  }),
  input: (base) => ({
    ...base,
    fontSize: '16px',
    color: '#1A1A1A',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({
    ...base,
    color: '#929292',
    '&:hover': { color: '#929292' },
  }),
  menu: (base) => ({
    ...base,
    // The menu portals to document.body, outside the modal's Nunito wrapper —
    // without this it inherits the global Open Sans.
    fontFamily: '"Nunito Sans", sans-serif',
    border: '1px solid #D6D6D6',
    borderRadius: '4px',
    boxShadow: '0px 16px 24px rgba(48, 49, 51, 0.1), 0px 0px 1px rgba(48, 49, 51, 0.05)',
  }),
  menuList: (base) => ({
    ...base,
    padding: '16px',
  }),
  option: (base, state) => ({
    ...base,
    padding: '4px',
    borderRadius: '4px',
    fontSize: '16px',
    lineHeight: 1.5,
    color: state.isDisabled ? '#929292' : '#1A1A1A',
    backgroundColor: state.isFocused && !state.isDisabled ? '#F2F2F2' : 'transparent',
    ':not(:last-of-type)': { marginBottom: '16px' },
    ':active': { backgroundColor: '#F2F2F2' },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 10000 }),
};

// Centers the 16px glyph inside its 24px slot.
const ICON_BOX = {
  width: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const STEP_DEFS = [
  { n: 1, label: 'Choose Exercise' },
  { n: 2, label: 'Choose Exercise Details' },
  { n: 3, label: 'Confirm & Merge' },
];

const MergeExerciseModal = ({
  isOpen, onClose, sourceExercise, candidates, accountCode, onMerged, onPlayVideo,
}) => {
  const dispatch = useDispatch();
  const [step, setStep] = useState(1);
  // The chosen merge target (the exercise that survives).
  const [survivorId, setSurvivorId] = useState(null);
  // Per conflicting field -> id of the exercise whose value we keep.
  const [choices, setChoices] = useState({});
  const [isMerging, setIsMerging] = useState(false);

  const survivor = candidates.find((c) => c.id === survivorId) || null;
  // The source/target pair once both are known — everything below (conflicts,
  // preview, removal) is scoped to just this pair.
  const pair = useMemo(
    () => (sourceExercise && survivor ? [sourceExercise, survivor] : []),
    [sourceExercise, survivor],
  );
  const byId = (id) => pair.find((e) => e.id === id) || null;

  const typeLabel = TYPE_LABELS[sourceExercise?.type] || 'exercise';

  // Grouped select options: same-type candidates first (selectable), the rest
  // disabled — a merge target must share the source's type.
  const targetOptions = useMemo(() => {
    if (!sourceExercise) return [];
    const sameType = candidates.filter((c) => c.type === sourceExercise.type);
    const otherType = candidates.filter((c) => c.type !== sourceExercise.type);
    return [
      { label: typeLabel, options: sameType },
      { label: 'Other types', options: otherType },
    ];
  }, [candidates, sourceExercise, typeLabel]);

  const defaultChoices = (sid) => {
    const target = candidates.find((c) => c.id === sid) || null;
    const candidatePair = sourceExercise && target ? [sourceExercise, target] : [];
    const next = {};
    FIELDS.forEach((f) => {
      // Keep the target's value; if it's empty, fall back to the source's.
      let src = target;
      if (!target || f.empty(target)) {
        const withVal = candidatePair.find((e) => !f.empty(e));
        if (withVal) src = withVal;
      }
      next[f.key] = src ? src.id : sid;
    });
    return next;
  };

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSurvivorId(null);
      setChoices({});
      setIsMerging(false);
    }
  }, [isOpen, sourceExercise]);

  const setSurvivor = (id) => {
    setSurvivorId(id);
    setChoices(defaultChoices(id));
  };

  const setChoice = (key, id) => setChoices((p) => ({ ...p, [key]: id }));

  // Fields whose value differs between the source and the chosen target.
  const conflictFields = useMemo(() => FIELDS.filter((f) => {
    const vals = new Set(pair.map((e) => (f.empty(e) ? '' : f.get(e))));
    return vals.size > 1;
  }), [pair]);
  const hasConflicts = conflictFields.length > 0;

  const merged = useMemo(() => {
    if (!survivor) return null;
    const valueFrom = (f) => f.get(byId(choices[f.key]) || survivor);
    const tags = [...new Map(
      pair.flatMap((e) => e.tags || []).map((t) => [t.id ?? t.name, t]),
    ).values()];
    const attrs = [...new Set(pair.flatMap((e) => ATTRIBUTE_DEFS
      .filter((a) => (e.defaultOptions || {})[a.key])
      .map((a) => a.label)))];
    return {
      name: valueFrom(FIELDS[0]),
      tracking: valueFrom(FIELDS[1]),
      description: valueFrom(FIELDS[2]),
      video: valueFrom(FIELDS[3]),
      tags: tags.map((t) => t.name).filter(Boolean),
      attrs,
    };
    // byId is a fresh function every render but only reads `pair`, which is
    // already listed — adding it would recompute this memo every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survivor, choices, pair]);

  const goBack = () => {
    if (step === 3 && !hasConflicts) setStep(1);
    else if (step > 1) setStep(step - 1);
  };

  const goNext = () => {
    if (step === 1) setStep(hasConflicts ? 2 : 3);
    else if (step === 2) setStep(3);
  };

  const handleMerge = () => {
    if (!survivor || !sourceExercise) return;
    const removedIds = [sourceExercise.id];
    setIsMerging(true);
    dispatch(mergeExercises(accountCode, {
      survivorId,
      mergeIds: removedIds,
      keep: {
        name: choices.name,
        tracking: choices.tracking,
        description: choices.description,
        video: choices.video,
      },
    }, {
      onSuccess: () => {
        setIsMerging(false);
        onMerged(removedIds, merged.name, survivorId);
        onClose();
      },
      // Thunk toasts the error; the single source->target pair means there is
      // no partial-progress state to report, so just re-enable the wizard.
      onError: () => setIsMerging(false),
    }));
  };

  // De-dupe options by display value across the source/target pair, source
  // first so every question reads left-to-right in the same order.
  const optionFor = (f) => {
    const seen = {};
    const opts = [];
    pair.forEach((e) => {
      const val = f.get(e);
      if (seen[val]) return;
      const o = { value: val, id: e.id };
      seen[val] = o;
      opts.push(o);
    });
    return opts;
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      hideTitle
      hideButton
      customMaxWidth='700px'
      customMinHeight='0'
      customPadding='0'
      customBorderRadius='8px'
    >
      {sourceExercise && (
        <Body>
          <HeaderRow>
            <Title>Merge Exercises</Title>
            <CloseButton type='button' onClick={onClose} aria-label='Close'>
              <Icon icon='remove' size={16} color='#929292' />
            </CloseButton>
          </HeaderRow>

          <Stepper>
            {STEP_DEFS.map((d, i) => {
              const done = step > d.n;
              const reached = step >= d.n;
              return (
                <React.Fragment key={d.n}>
                  <StepGroup>
                    <StepBubble reached={reached}>
                      {done ? <Icon icon='checkmark' size={16} color='#FFFFFF' /> : d.n}
                    </StepBubble>
                    <StepLabel reached={reached}>{d.label}</StepLabel>
                  </StepGroup>
                  {i < STEP_DEFS.length - 1 && <StepConnector done={step > d.n} />}
                </React.Fragment>
              );
            })}
          </Stepper>

          <Divider />

          {step === 1 && (
            <StepBody>
              <FieldGroup>
                <FieldLabel>Selected exercise</FieldLabel>
                <ChoiceRow>
                  <SourceBox title={sourceExercise.name}>
                    {sourceExercise.name || 'Untitled exercise'}
                  </SourceBox>
                  <Icon icon='remove' size={16} color='#FF0505' styledProps={ICON_BOX} />
                  <OutcomeText>Will be deleted</OutcomeText>
                </ChoiceRow>
              </FieldGroup>

              <FieldGroup gap='2px'>
                <FieldLabel>Which exercise do you want to merge to?</FieldLabel>
                <ChoiceRow>
                  <SelectWrap>
                    <Select
                      classNamePrefix='merge-target-select'
                      options={targetOptions}
                      value={survivor}
                      getOptionLabel={(o) => o.name}
                      getOptionValue={(o) => o.id}
                      isOptionDisabled={(o) => o.type !== sourceExercise.type}
                      isClearable
                      backspaceRemovesValue
                      onChange={(o) => setSurvivor(o ? o.id : null)}
                      placeholder='Search exercises...'
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                    />
                  </SelectWrap>
                  <Icon icon='checkmark' size={16} color='#0DCC8A' styledProps={ICON_BOX} />
                  <OutcomeText>Will be kept</OutcomeText>
                </ChoiceRow>
                <HelperText>Exercises must be the same type.</HelperText>
              </FieldGroup>
            </StepBody>
          )}

          {step === 2 && (
            <StepBody wide>
              <FieldGroup gap='8px'>
                <IntroText>Select the details you would like to keep.</IntroText>
                {conflictFields.length > 0 && (
                  <ConflictField
                    field={conflictFields[0]}
                    options={optionFor(conflictFields[0])}
                    choices={choices}
                    byId={byId}
                    setChoice={setChoice}
                    onPlayVideo={onPlayVideo}
                  />
                )}
              </FieldGroup>
              {conflictFields.slice(1).map((f) => (
                <ConflictField
                  key={f.key}
                  field={f}
                  options={optionFor(f)}
                  choices={choices}
                  byId={byId}
                  setChoice={setChoice}
                  onPlayVideo={onPlayVideo}
                />
              ))}
            </StepBody>
          )}

          {step === 3 && merged && (
            <StepBody>
              <FieldGroup>
                <FieldLabel>This exercise will be permanently removed:</FieldLabel>
                <SummaryCard>
                  <Icon icon='remove' size={16} color='#FF0505' styledProps={ICON_BOX} />
                  <SummaryName>{sourceExercise.name || 'Untitled exercise'}</SummaryName>
                </SummaryCard>
              </FieldGroup>

              <FieldGroup gap='8px'>
                <FieldGroup>
                  <FieldLabel>This exercise will be kept with the following changes:</FieldLabel>
                  <KeptCard>
                    <KeptHead>
                      <Icon icon='checkmark' size={16} color='#0DCC8A' styledProps={ICON_BOX} />
                      <SummaryName>{merged.name}</SummaryName>
                    </KeptHead>
                    <KeptDetails>
                      <DetailLabels>
                        <div>Tracking</div>
                        <div>Description</div>
                        <div>Video</div>
                        <div>Tags</div>
                        <div>Attributes</div>
                      </DetailLabels>
                      <DetailValues>
                        <div>{merged.tracking}</div>
                        <div>{merged.description}</div>
                        <div>{merged.video}</div>
                        {merged.tags.length ? (
                          <TagChips>
                            {merged.tags.map((t) => <TagChip key={t}>{t}</TagChip>)}
                          </TagChips>
                        ) : (
                          <div>-</div>
                        )}
                        <div>{merged.attrs.join(', ') || '-'}</div>
                      </DetailValues>
                    </KeptDetails>
                  </KeptCard>
                </FieldGroup>
                <NoteText>Athlete history will be updated automatically.</NoteText>
              </FieldGroup>
            </StepBody>
          )}

          <Divider />

          <FooterBar>
            {step === 1 ? (
              <FooterButton type='button' onClick={onClose}>Cancel</FooterButton>
            ) : (
              <FooterButton type='button' onClick={goBack}>Back</FooterButton>
            )}
            {step < 3 ? (
              <FooterButton
                type='button'
                primary
                disabled={step === 1 && !survivorId}
                onClick={goNext}
              >
                Next
              </FooterButton>
            ) : (
              <FooterButton
                type='button'
                primary
                disabled={isMerging}
                onClick={handleMerge}
              >
                Merge Exercises
                {isMerging && <Spinner large={false} saving />}
              </FooterButton>
            )}
          </FooterBar>
        </Body>
      )}
    </Modal>
  );
};

// Step-2 preview tile. YouTube URLs use the public thumbnail service; other
// URLs get a real first frame, falling back to an iframe.ly thumbnail, then the dimmed tile.
const VideoPreviewTile = ({ videoSrc, onPlay }) => {
  const [thumb, setThumb] = useState(() => getYoutubeThumb(videoSrc));
  const [fileFailed, setFileFailed] = useState(false);

  const handleFileError = () => {
    setFileFailed(true);
    fetchEmbedThumb(videoSrc).then((href) => {
      if (href) setThumb(href);
    });
  };

  return (
    <VideoPreview
      thumb={thumb}
      role='button'
      tabIndex={0}
      aria-label='Preview video'
      onClick={onPlay}
      onKeyDown={(ev) => {
        if (ev.key === 'Enter' || ev.key === ' ') onPlay(ev);
      }}
    >
      {!thumb && !fileFailed && (
        <>
          <FirstFrame
            src={`${videoSrc}#t=0.1`}
            preload='metadata'
            muted
            playsInline
            onError={handleFileError}
          />
          <FrameShade />
        </>
      )}
      <PlayIconWrap>
        <Icon icon='play' size={24} color='#FFFFFF' />
      </PlayIconWrap>
    </VideoPreview>
  );
};

VideoPreviewTile.propTypes = {
  videoSrc: PropTypes.string.isRequired,
  onPlay: PropTypes.func.isRequired,
};

// One resolve-step question: the field label and its two side-by-side value
// cards. The selected option's value reads bold.
const ConflictField = ({
  field, options, choices, byId, setChoice, onPlayVideo,
}) => (
  <FieldGroup>
    <FieldLabel>{field.label}</FieldLabel>
    <OptionList>
      {options.map((o) => {
        // Options are de-duped by value, so the picked source's value
        // identifies the selected option.
        const chosen = byId(choices[field.key]);
        const selected = !!chosen && field.get(chosen) === o.value;
        const optionExercise = byId(o.id);
        const isVideoOption = field.key === 'video' && hasVideo(optionExercise);
        const videoSrc = isVideoOption ? decodeVideoUrl(optionExercise.videoUrl) : null;
        const valueText = isVideoOption ? `Video from “${optionExercise.name}”` : o.value;
        const playVideo = (ev) => {
          // The tile lives inside the option's radio card — don't let the
          // preview click double as picking the option.
          ev.stopPropagation();
          onPlayVideo(optionExercise.videoUrl);
        };
        return (
          <OptionCard
            key={o.id}
            type='button'
            selected={selected}
            onClick={() => setChoice(field.key, o.id)}
          >
            <OptionRow>
              <Radio selected={selected} />
              <OptionValue>{valueText}</OptionValue>
            </OptionRow>
            {isVideoOption && (
              <VideoPreviewTile videoSrc={videoSrc} onPlay={playVideo} />
            )}
          </OptionCard>
        );
      })}
    </OptionList>
  </FieldGroup>
);

ConflictField.propTypes = {
  field: PropTypes.shape({
    key: PropTypes.string,
    label: PropTypes.string,
    get: PropTypes.func,
  }).isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    id: PropTypes.number,
  })).isRequired,
  choices: PropTypes.shape({}).isRequired,
  byId: PropTypes.func.isRequired,
  setChoice: PropTypes.func.isRequired,
  onPlayVideo: PropTypes.func,
};

ConflictField.defaultProps = {
  onPlayVideo: () => {},
};

MergeExerciseModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  accountCode: PropTypes.number,
  sourceExercise: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    type: PropTypes.string,
    track: PropTypes.number,
    description: PropTypes.string,
    videoUrl: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.shape({})),
    defaultOptions: PropTypes.shape({}),
  }),
  candidates: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    type: PropTypes.string,
    track: PropTypes.number,
    description: PropTypes.string,
    videoUrl: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.shape({})),
    defaultOptions: PropTypes.shape({}),
  })),
  onMerged: PropTypes.func,
  onPlayVideo: PropTypes.func,
};

MergeExerciseModal.defaultProps = {
  accountCode: undefined,
  sourceExercise: null,
  candidates: [],
  onMerged: () => {},
  onPlayVideo: () => {},
};

export default MergeExerciseModal;
